<script setup>
/**
 * 共享底部导航，逐条对齐旧版 web/34.a2d07e8cea2477812f5a.js 里的
 * app-tabs 组件（类 d、模板工厂 s + 子模板 h、样式 p）。
 *
 * 旧版模板结构：
 *   ion-tabs (ionTabButtonClick → select($event.detail.tab))
 *     ion-tab-bar[slot=bottom]
 *       ion-tab-button[tab=home]      > ion-icon[name=home]     + ion-label「彩票大厅」
 *       ion-tab-button[tab=bet]       > ion-icon[name=podium]   + ion-label「投注概况」
 *       *ngIf="recommend" →
 *       ion-tab-button[tab=recommend] > ion-icon[name=contacts] + ion-label「推广」
 *       ion-tab-button[tab=user]      > ion-icon[name=person]   + ion-label「个人中心」
 *
 * 注意「推广」这一项是**条件渲染**的：
 *   ngOnInit → getGameRecommend() → getConfig('game_recommend')
 *   拿到的值存进 this.recommend，为真才显示这个 tab。
 * 组件自己的样式表 p 是空字符串，所以整条 tab 栏完全走 Ionic 默认样式。
 *
 * 旧版 tabs 子路由（同一文件里的路由表）与新版页面的对应关系：
 *   /tabs/home      → HomePageModule                        → 新版 /home
 *   /tabs/bet       → ../game/bet/bet.module#BetPageModule   → 新版 /user/bets
 *        ↑ 这就是 chunk 11 的 app-bet，也就是个人中心「游戏记录」那一页，
 *          「投注概况」和「游戏记录」在旧版是同一个页面，只是入口不同
 *   /tabs/recommend → ../user/recommend/...#RecommendPageModule → 新版 /user/recommend
 *   /tabs/user      → ../user/user.module#UserPageModule     → 新版 /user/profile
 *
 * 样式取自 web/89.389c9bf252ef5a2aee96.js（ion-tab-bar / ion-tab-button 的 md 那套）。
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { request } from '../services/api'

const route = useRoute()

// 旧版 this.recommend = false，getConfig('game_recommend') 返回真值才显示「推广」tab
const recommend = ref(false)

/** 旧版 getGameRecommend() */
async function getGameRecommend() {
  try {
    const response = await request('/config/detail', { method: 'POST', data: { name: 'game_recommend' } })
    // 旧版就是一句 `0 === error_code && (this.recommend = l.data)`，然后模板 *ngIf="recommend"。
    // Angular 的 ngIf 走 JS 真值判断，所以后端如果返回字符串 '0'，旧版同样会显示「推广」tab
    //（字符串 '0' 在 JS 里是 truthy）。这里照抄旧版行为，不做额外的 '0' 排除。
    recommend.value = Boolean(response.data)
  } catch (_) {
    // 旧版这里 error_code 非 0 时不做任何处理，recommend 保持 false
  }
}

const tabs = computed(() => {
  const list = [
    { tab: 'home', label: '彩票大厅', to: '/home' },
    { tab: 'bet', label: '投注概况', to: '/user/bets' },
  ]
  // 旧版 *ngIf="recommend"
  if (recommend.value) list.push({ tab: 'recommend', label: '推广', to: '/user/recommend' })
  list.push({ tab: 'user', label: '个人中心', to: '/user/profile' })
  return list
})

/** 当前选中哪个 tab（旧版靠 ion-tabs 自己维护 selectedTab，这里按路由推断） */
const activeTab = computed(() => {
  const path = route.path
  if (path.startsWith('/user/bets')) return 'bet'
  if (path.startsWith('/user/recommend')) return 'recommend'
  if (path.startsWith('/user')) return 'user'
  return 'home'
})

onMounted(() => { void getGameRecommend() })
</script>

<template>
  <!-- 旧版 ion-tab-bar[slot=bottom]，role=tablist -->
  <nav class="ion-tab-bar" role="tablist">
    <RouterLink
      v-for="item in tabs"
      :key="item.tab"
      class="ion-tab-button tab-layout-icon-top tab-has-label tab-has-icon"
      :class="{ 'tab-selected': activeTab === item.tab }"
      :to="item.to"
      role="tab"
      :aria-selected="activeTab === item.tab ? 'true' : null"
    >
      <!-- 旧版 ion-icon，md 模式下取的是 web/svg/md-*.svg -->
      <svg v-if="item.tab === 'home'" class="ion-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M208 448V320h96v128h97.6V256H464L256 64 48 256h62.4v192z" fill="currentColor" /></svg>
      <svg v-else-if="item.tab === 'bet'" class="ion-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M32 224h128v192H32zM192 128h128v288H192zM352 288h128v128H352z" fill="currentColor" /></svg>
      <svg v-else-if="item.tab === 'recommend'" class="ion-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M239.208 343.937c-17.78 10.103-38.342 15.876-60.255 15.876-21.909 0-42.467-5.771-60.246-15.87C71.544 358.331 42.643 406 32 448h293.912c-10.639-42-39.537-89.683-86.704-104.063zM178.953 120.035c-58.479 0-105.886 47.394-105.886 105.858 0 58.464 47.407 105.857 105.886 105.857s105.886-47.394 105.886-105.857c0-58.464-47.408-105.858-105.886-105.858zm0 186.488c-33.671 0-62.445-22.513-73.997-50.523H252.95c-11.554 28.011-40.326 50.523-73.997 50.523z" fill="currentColor" /><path d="M322.602 384H480c-10.638-42-39.537-81.691-86.703-96.072-17.781 10.104-38.343 15.873-60.256 15.873-14.823 0-29.024-2.654-42.168-7.49-7.445 12.47-16.927 25.592-27.974 34.906C289.245 341.354 309.146 364 322.602 384zM306.545 200h100.493c-11.554 28-40.327 50.293-73.997 50.293-8.875 0-17.404-1.692-25.375-4.51a128.411 128.411 0 0 1-6.52 25.118c10.066 3.174 20.779 4.862 31.895 4.862 58.479 0 105.886-47.41 105.886-105.872 0-58.465-47.407-105.866-105.886-105.866-37.49 0-70.427 19.703-89.243 49.09C275.607 131.383 298.961 163 306.545 200z" fill="currentColor" /></svg>
      <svg v-else class="ion-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 256c52.805 0 96-43.201 96-96s-43.195-96-96-96-96 43.201-96 96 43.195 96 96 96zm0 48c-63.598 0-192 32.402-192 96v48h384v-48c0-63.598-128.402-96-192-96z" fill="currentColor" /></svg>
      <!-- 旧版 ion-label -->
      <span class="ion-label">{{ item.label }}</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
/* --- ion-tab-bar（md），逐条照抄 web/89 --- */
.ion-tab-bar{
  --background:var(--ion-tab-bar-background,var(--ion-background-color,#fff));
  --border:1px solid var(--ion-tab-bar-border-color,var(--ion-border-color,rgba(0,0,0,.07)));
  --color:var(--ion-tab-bar-color,#666);
  /* 旧版这里是 var(--ion-color-primary)，主题把它设成了 #f95454（见 base.css 注释） */
  --color-selected:var(--ion-tab-bar-color-activated,var(--legacy-theme,#3880ff));
  display:flex;align-items:center;justify-content:center;width:auto;
  padding-bottom:var(--ion-safe-area-bottom,0);
  border-top:var(--border);
  background:var(--background);color:var(--color);
  text-align:center;contain:strict;user-select:none;z-index:10;
  box-sizing:content-box!important;
  height:56px;
  /* 旧版这条 tab 栏由 ion-tabs 固定在底部 */
  position:fixed;left:0;right:0;bottom:0;
}

/* --- ion-tab-button（md），逐条照抄 web/89。默认 layout 是 icon-top --- */
.ion-tab-button{
  --padding-top:0;--padding-end:12px;--padding-bottom:0;--padding-start:12px;
  flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
  height:100%;max-width:168px;
  padding:0 12px;border:0;background:transparent;
  color:inherit;text-decoration:none;cursor:pointer;overflow:hidden;box-sizing:border-box;
  font-size:12px;font-weight:400;letter-spacing:.03em;
}
/* 旧版 :host(.tab-selected){color:var(--color-selected)} */
.ion-tab-button.tab-selected{color:var(--color-selected)}

/* 旧版 ::slotted(ion-icon) / ::slotted(ion-label) */
.ion-tab-button .ion-icon,.ion-tab-button .ion-label{
  display:block;align-self:center;max-width:100%;
  text-overflow:ellipsis;white-space:nowrap;overflow:hidden;box-sizing:border-box;
}
/* icon 在上、label 在下 */
.ion-tab-button .ion-icon{order:-1;width:1em;height:1em;font-size:22px}
.ion-tab-button .ion-label{order:0;text-transform:none}
/* 旧版 .tab-layout-icon-top 下的外边距 */
.ion-tab-button.tab-layout-icon-top .ion-icon{margin:6px 0 2px}
.ion-tab-button.tab-layout-icon-top .ion-label{margin:0 0 6px}
</style>
